import { createApp } from 'vue'
import { createPinia, setActivePinia } from 'pinia'
import VuePdfApp from 'vue3-pdf-app'
import App from './App.vue'
import router from './router'
import registerFontAwesome from './plugins/fontAwesome'
import { PerfectScrollbarPlugin } from 'vue3-perfect-scrollbar'
import 'vue3-perfect-scrollbar/style.css'
// @vuepic/vue-datepicker (Ersatz für vue2-datepicker): Basis-Styles global laden,
// konsistent zur perfect-scrollbar-CSS-Einbindung. Die VME-Theme-Overrides für die
// `.dp__*`-Klassen liegen in src/assets/scss/global/global.scss.
import '@vuepic/vue-datepicker/dist/main.css'
import { formatDate } from './libs/dateFormat'
import api from './services/api'
import configureMsal from './plugins/msalConfig'

// import * as Sentry from '@sentry/vue'
// import { BrowserTracing } from '@sentry/tracing'
// Sentry.init({
//   Vue,
//   dsn: 'https://4e14de5bb0397f3cc7500fc3294ba8a6@o4508943312355328.ingest.de.sentry.io/4508943315435600',
//   integrations: [
//     new BrowserTracing({
//       routingInstrumentation: Sentry.vueRouterInstrumentation(router),
//       tracingOrigins: ['katalogwand.meinvme.de', /^https:\/\/katalogwand\.meinvme\.de/, 'katalogwand-staging.meinvmeint.de', /^https:\/\/katalogwand-staging\.meinvmeint\.de/, /^\//]
//     })
//   ],
//   tracesSampleRate: 1.0
// })

/**
 * Einmalige Bereinigung: Bis zur Umstellung auf `sessionStorage` lagen Identitaet,
 * Firma, Rollen und der komplette MSAL-Cache im `localStorage` und ueberdauerten
 * damit das Schliessen des Browsers. Ohne dieses Aufraeumen blieben diese
 * Eintraege bei jedem bestehenden Nutzer dauerhaft zurueck — inklusive der
 * MSAL-Tokens.
 *
 * Verlorene Daten entstehen dabei nicht: `companyId` wird ueber
 * `getCurrentMerchant()` aus der Datenbank nachgeladen, die Rollen ueber
 * `selectAccount()`, die Identitaet aus dem Token.
 */
function purgeLegacyLocalStorage() {
  try {
    const appKeys = ['userId', 'companyId', 'userRoles', 'api_token', 'DEV_MSAL_BYPASS']
    appKeys.forEach((key) => localStorage.removeItem(key))
    // MSAL legt seine Eintraege unter wechselnden, zusammengesetzten Schluesseln ab.
    Object.keys(localStorage)
      .filter((key) => key.startsWith('msal.') || key.includes('login.windows.net') || key.includes('.msal.'))
      .forEach((key) => localStorage.removeItem(key))
  } catch (e) {
    // localStorage kann blockiert sein (private Modi, Richtlinien) — kein Grund,
    // den Start der Anwendung zu verhindern.
    console.warn('Aufraeumen des localStorage nicht moeglich:', e)
  }
}

async function bootstrap() {
  purgeLegacyLocalStorage()

  const app = createApp(App)

  const pinia = createPinia()
  // Aktive Pinia-Instanz auch für Nicht-Setup-Kontexte (axios-Interceptor,
  // router.beforeEach, direkte useAppStore()-Aufrufe) sicherstellen.
  setActivePinia(pinia)
  app.use(pinia)

  app.use(router)

  // Globale Registrierungen (früher via `Vue.*` global) -> app-scoped.
  registerFontAwesome(app)
  // vue3-pdf-app (Ersatz für das Vue2-only vue-pdf-app) ist eine echte Vue-3-Komponente.
  // Gleiche Props/Slots/Events wie vue-pdf-app (config, page-scale, id-config, pdf, @open)
  // — PdfView.vue bleibt unverändert.
  app.component('vue-pdf-app', VuePdfApp)
  app.use(PerfectScrollbarPlugin)

  // Frühere `Vue.prototype.*` -> `app.config.globalProperties.*`
  app.config.globalProperties.$api = api
  app.config.globalProperties.$scrollToTop = () => window.scrollTo(0, 0)
  // vue-moment entfernt: Datumsformatierung läuft direkt über luxon (src/libs/dateFormat).
  // `$formatDate` ersetzt die bisherigen `$moment(...).format('DD.MM.YYYY')`-Aufrufe in
  // den Templates; die Ausgabe (deutsches Format) bleibt unverändert.
  app.config.globalProperties.$formatDate = formatDate

  // MSAL konfigurieren (async initialize in @azure/msal-browser v3+); auf dem
  // Dev-Bypass wird nicht initialisiert/redirected.
  await configureMsal(app)

  app.mount('#app')
}

bootstrap()
